import React, { useEffect, useState } from 'react';
import { Alert, Button, Empty, InputNumber, Modal, Spin, Table, Typography } from 'antd';
import { ImportableHolding, smartBetaApi, SmartBetaView } from '../../../services/smartBetaApi';
import { formatNumber, numberInputProps } from '../../../utils/numberFormat';
import { useIsMobile } from '../../../hooks/useIsMobile';

const { Text } = Typography;

interface Props {
  open: boolean;
  config: SmartBetaView;
  onCancel: () => void;
  onDone: (view: SmartBetaView) => void;
}

/** Take shares from Not trading into the smart beta, at the broker's average cost. */
const SmartBetaImportModal: React.FC<Props> = ({ open, config, onCancel, onDone }) => {
  const isMobile = useIsMobile();
  const [rows, setRows] = useState<ImportableHolding[] | null>(null);
  const [qty, setQty] = useState<Record<string, number | null>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setRows(null);
    setQty({});
    setError(null);
    smartBetaApi
      .importable(config.dataConfigId)
      .then(setRows)
      .catch((e) => {
        setRows([]);
        setError(e?.response?.data?.message ?? e?.message ?? String(e));
      });
  }, [open, config.dataConfigId]);

  const basket = new Set(config.settings.whitelist.map((w) => w.symbol));
  const chosen = (rows ?? []).filter((r) => (qty[r.symbol] ?? 0) > 0);
  const value = chosen.reduce((s, r) => s + (qty[r.symbol] ?? 0) * (r.averagePrice ?? 0), 0);
  const outside = chosen.filter((r) => !basket.has(r.symbol)).map((r) => r.symbol);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      onDone(
        await smartBetaApi.importHoldings(
          config.dataConfigId,
          chosen.map((r) => ({ symbol: r.symbol, quantity: qty[r.symbol] ?? 0 })),
        ),
      );
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message ?? err.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Import from Not trading"
      open={open}
      onCancel={onCancel}
      onOk={submit}
      okText={chosen.length ? `Import ${chosen.length} ticker${chosen.length > 1 ? 's' : ''}` : 'Import'}
      okButtonProps={{ loading: busy, disabled: chosen.length === 0 }}
      width={isMobile ? '100%' : 620}
      style={isMobile ? { top: 'auto', bottom: 0, margin: 0, maxWidth: '100vw', paddingBottom: 0 } : undefined}
      destroyOnClose
    >
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
        Shares you hold that no strategy is trading. They join the smart beta at the broker&apos;s average cost; the
        next rebalance brings them to your weights.
      </Text>
      {rows === null ? (
        <Spin />
      ) : rows.length === 0 ? (
        <Empty description="Nothing to import: every share is already in a strategy" />
      ) : (
        <Table<ImportableHolding>
          size="small"
          rowKey="symbol"
          pagination={false}
          scroll={{ x: true }}
          dataSource={rows}
          columns={[
            { title: 'Ticker', dataIndex: 'symbol' },
            { title: 'Available', align: 'right', render: (_, r) => formatNumber(r.quantity) },
            {
              title: 'Avg cost',
              align: 'right',
              render: (_, r) => (r.averagePrice == null ? 'market price' : formatNumber(r.averagePrice)),
            },
            {
              title: 'Import',
              render: (_, r) => (
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <InputNumber
                    {...numberInputProps()}
                    min={0}
                    max={r.quantity}
                    precision={0}
                    value={qty[r.symbol] ?? null}
                    onChange={(v) => setQty((q) => ({ ...q, [r.symbol]: v }))}
                    style={{ width: 110 }}
                    aria-label={`${r.symbol} quantity`}
                  />
                  <Button size="small" type="link" onClick={() => setQty((q) => ({ ...q, [r.symbol]: r.quantity }))}>
                    All
                  </Button>
                </div>
              ),
            },
          ]}
        />
      )}
      {chosen.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 13 }}>
          Brings in about <Text strong>{formatNumber(value)} VND</Text> of shares as capital.
        </div>
      )}
      {outside.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message={`${outside.join(', ')} ${outside.length > 1 ? 'are' : 'is'} not in your basket: the next rebalance sells ${outside.length > 1 ? 'them' : 'it'}.`}
        />
      )}
      {error && <Alert type="error" showIcon message={error} style={{ marginTop: 12 }} />}
    </Modal>
  );
};

export default SmartBetaImportModal;
